import { useEffect, useRef, useState } from "react";
import { ImageOff } from "lucide-react";
import { resolveImage, sizedImage } from "@/lib/asset-map";
import { cn } from "@/lib/utils";

type Props = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  src?: string | null;
  alt: string;
  /** Container className applied to the wrapper that holds the fallback. */
  wrapperClassName?: string;
  /** Painted width in CSS pixels; used to request a right-sized remote copy. */
  targetWidth?: number;
};

/**
 * Renders an image with graceful fallback for missing / broken sources.
 * - Empty/null source → placeholder block
 * - onError (404, CORS, decode failure) → placeholder block
 * - Remote originals are downscaled to the width actually painted
 */
export function SmartImage({ src, alt, className, wrapperClassName, targetWidth = 800, ...rest }: Props) {
  const [stage, setStage] = useState<0 | 1 | 2>(0);
  const imageRef = useRef<HTMLImageElement>(null);
  const raw = resolveImage(src ?? "");
  const resolved = stage === 0 ? sizedImage(src ?? "", targetWidth) : raw;
  const showFallback = !resolved || stage === 2;

  useEffect(() => { setStage(0); }, [src]);
  useEffect(() => {
    // SSR images can fail before hydration attaches onError. Inspect that
    // settled state too, so a failed CDN copy still falls back to its original.
    const image = imageRef.current;
    if (image?.complete && image.naturalWidth === 0) {
      setStage((s) => (s === 0 ? 1 : 2));
    }
  }, [resolved]);

  if (showFallback) {
    return (
      <div
        className={cn(
          "flex h-full w-full items-center justify-center bg-muted text-muted-foreground",
          wrapperClassName,
          className,
        )}
        aria-label={alt}
        role="img"
      >
        <ImageOff className="h-8 w-8 opacity-50" />
      </div>
    );
  }

  return (
    <img
      ref={imageRef}
      loading="lazy"
      decoding="async"
      {...rest}
      src={resolved}
      alt={alt}
      onError={() => setStage((s) => (s === 0 ? 1 : 2))}
      className={className}
    />
  );
}
